import { leadQueriesApi } from '@/lib/api';
import type { LeadQueryMessage } from '@/types/lead-query';

export const MESSAGE_PAGE_LIMIT = 50;

/** Messages are oldest→newest per page; page 1 is oldest slice. Loads the newest slice for chat UI. */
export async function fetchLatestMessageSlice(queryId: string, limit = MESSAGE_PAGE_LIMIT) {
  const first = await leadQueriesApi.getMessages(queryId, { page: 1, limit });
  if (first.statusCode !== 200) {
    throw new Error((first as { message?: string }).message || 'Failed to load messages');
  }
  const total = first.data.pagination.total;
  if (total === 0) {
    return {
      messages: [] as LeadQueryMessage[],
      lastPage: 1,
      oldestLoadedPage: 1,
      total: 0,
    };
  }
  const lastPage = Math.max(1, Math.ceil(total / limit));
  if (lastPage === 1) {
    return {
      messages: first.data.messages as LeadQueryMessage[],
      lastPage: 1,
      oldestLoadedPage: 1,
      total,
    };
  }
  const last = await leadQueriesApi.getMessages(queryId, { page: lastPage, limit });
  if (last.statusCode !== 200) {
    throw new Error((last as { message?: string }).message || 'Failed to load messages');
  }
  return {
    messages: last.data.messages as LeadQueryMessage[],
    lastPage,
    oldestLoadedPage: lastPage,
    total,
  };
}

/** Merge server slice into existing list (keeps loaded older pages). Dedupes by messageId, sorts by time. */
export function mergeLeadMessages(
  prev: LeadQueryMessage[],
  latestSlice: LeadQueryMessage[]
): LeadQueryMessage[] {
  const byId = new Map(prev.map((m) => [m.messageId, m]));
  for (const m of latestSlice) {
    if (!byId.has(m.messageId)) byId.set(m.messageId, m);
  }
  return Array.from(byId.values()).sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );
}
